<template>
  <SidebarMenu>
    <SidebarMenuItem>
      <DropdownMenu>
        <DropdownMenuTrigger as-child>
          <SidebarMenuButton
            size="lg"
            class="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
          >
            <Avatar class="size-8 rounded-lg">
              <AvatarImage :src="user.avatar" :alt="user.name" />
              <AvatarFallback class="rounded-lg"><User /></AvatarFallback>
            </Avatar>
            <div class="grid flex-1 text-left text-sm leading-tight">
              <span class="truncate font-medium">{{ user.name }}</span>
              <span class="truncate text-xs">{{ user.email }}</span>
            </div>
            <ChevronsUpDown class="ml-auto size-4" />
          </SidebarMenuButton>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          class="w-[--reka-dropdown-menu-trigger-width] min-w-56 rounded-lg"
          align="end"
          :side-offset="4"
        >
          <DropdownMenuLabel class="p-0 font-normal">
            <div class="flex items-center gap-2 px-1 py-1.5 text-left text-sm">
              <Avatar class="size-8 rounded-lg">
                <AvatarImage :src="user.avatar" :alt="user.name" />
                <AvatarFallback class="rounded-lg"><User /></AvatarFallback>
              </Avatar>
              <div class="grid flex-1 text-left text-sm leading-tight">
                <span class="truncate font-semibold">{{ user.name }}</span>
                <span class="truncate text-xs">{{ user.email }}</span>
              </div>
            </div>
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          <!-- Language Switcher Sub Menu -->
          <DropdownMenuGroup>
            <DropdownMenuSub>
              <DropdownMenuSubTrigger class="cursor-pointer">
                <Languages class="mr-2 size-4" />
                {{ $t('common.language') }}
              </DropdownMenuSubTrigger>
              <DropdownMenuSubContent>
                <DropdownMenuItem
                  v-for="lang in languages"
                  :key="lang.code"
                  @click="onSelectLocale(lang.code)"
                  :aria-checked="locale === lang.code"
                  role="menuitemradio"
                  class="cursor-pointer select-none flex items-center gap-2"
                >
                  <img
                    v-if="lang.flag"
                    :src="lang.flag"
                    :alt="`${lang.code.split('-')[1]} flag`"
                    class="size-4 rounded-full object-cover"
                  />
                  <span>{{ lang.label }}</span>
                </DropdownMenuItem>
              </DropdownMenuSubContent>
            </DropdownMenuSub>
          </DropdownMenuGroup>
          <DropdownMenuSeparator />
          <!-- Log Out -->
          <DropdownMenuItem @click="logout">
            <LogOut />
            {{ $t('auth.logout') }}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </SidebarMenuItem>
  </SidebarMenu>
</template>

<script setup lang="ts">
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { SidebarMenu, SidebarMenuButton, SidebarMenuItem } from '@/components/ui/sidebar'
import { useLanguages } from '@/lib/locale_utils'
import { logout } from '@/modules/auth/services/auth_service'
import { useStorage } from '@vueuse/core'
import { ChevronsUpDown, Languages, LogOut, User } from 'lucide-vue-next'
import { useI18n } from 'vue-i18n'

defineProps<{
  user: {
    name: string
    email: string
    avatar: string
  }
}>()

const { locale } = useI18n()

const languages = useLanguages()

const selectedLanguage = useStorage('locale', 'en-US')

function onSelectLocale(newLocale: string) {
  locale.value = newLocale
  selectedLanguage.value = newLocale
}
</script>
